import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import mcpService, { Mcp } from '../../services/mcpService';
import { filtrarMcpsQueCubren } from '../../utils/mcpRedondeo';

export interface SeleccionarMcpRedondeoDialogProps {
  open: boolean;
  onClose: () => void;
  /** Grupo MCP de la caja (cada caja opera con un grupo). */
  grupo: number;
  /** Si &gt; 0, solo lista MCP cuyo máximo cubre este importe. */
  importeAbsMinimo?: number;
  /** MCP elegido (ya marcado como redondeo). */
  onSelect: (mcp: Mcp) => void;
}

/**
 * Elige el medio de redondeo del grupo/caja.
 * Solo lista MCP con esRedondeo activo (y, si hay importe, que alcancen el máximo).
 */
const SeleccionarMcpRedondeoDialog: React.FC<SeleccionarMcpRedondeoDialogProps> = ({
  open,
  onClose,
  grupo,
  importeAbsMinimo = 0,
  onSelect,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [redondeos, setRedondeos] = useState<Mcp[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const autoSelectedRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);
  onSelectRef.current = onSelect;
  onCloseRef.current = onClose;

  const keyOf = (m: Mcp) => `${m.mcp}|${m.tipo}`;

  const load = useCallback(async () => {
    if (!open || !(Number(grupo) > 0)) return;
    setLoading(true);
    setSelectedKey('');
    autoSelectedRef.current = false;
    try {
      let listRed = await mcpService.listarMcpsRedondeo(grupo);
      const abs = Math.abs(Number(importeAbsMinimo) || 0);
      if (abs > 0) listRed = filtrarMcpsQueCubren(listRed, abs);
      setRedondeos(listRed);
      if (listRed.length >= 1) setSelectedKey(keyOf(listRed[0]));
    } catch {
      enqueueSnackbar('No se pudieron cargar los medios de redondeo', { variant: 'error' });
      setRedondeos([]);
    } finally {
      setLoading(false);
    }
  }, [open, grupo, importeAbsMinimo, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!open || loading || autoSelectedRef.current) return;
    if (redondeos.length === 1) {
      autoSelectedRef.current = true;
      onSelectRef.current(redondeos[0]);
      onCloseRef.current();
    }
  }, [open, loading, redondeos]);

  const handleConfirm = () => {
    if (!selectedKey) {
      enqueueSnackbar('Seleccioná un medio de cobro de redondeo', { variant: 'warning' });
      return;
    }
    const fromRed = redondeos.find((m) => keyOf(m) === selectedKey);
    if (!fromRed) {
      enqueueSnackbar('Medio no encontrado', { variant: 'warning' });
      return;
    }
    onSelect(fromRed);
    onClose();
  };

  const handleCrear = () => {
    onClose();
    navigate(`/tesoreria/medios-mcp/nuevo?esRedondeo=1&grupo=${encodeURIComponent(String(grupo))}`);
  };

  const sinNinguno = !loading && redondeos.length === 0;

  if (open && !loading && redondeos.length === 1) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {sinNinguno ? 'Sin medio de redondeo apto' : 'Elegir medio de redondeo'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {sinNinguno
                ? importeAbsMinimo > 0
                  ? `Ningún MCP con redondeo activo cubre ${importeAbsMinimo.toLocaleString('es-AR')} (revisá el máximo o creá uno).`
                  : 'Este grupo/caja no tiene ningún MCP con “redondeo” activado. Creá uno o activá el flag en un medio existente.'
                : 'Hay más de un medio con redondeo activo que alcanza el máximo. Seleccioná cuál usar.'}
            </Typography>

            {!sinNinguno && (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={selectedKey}
                  onChange={(e) => setSelectedKey(e.target.value)}
                >
                  {redondeos.map((m) => (
                    <FormControlLabel
                      key={keyOf(m)}
                      value={keyOf(m)}
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {m.mcp?.trim() || '—'} · {m.tipo}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.descripcion?.trim() || 'Sin descripción'}
                            {` · máx. ${m.limiteRedondeo != null ? m.limiteRedondeo : '—'}`}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        {sinNinguno ? (
          <Button variant="contained" onClick={handleCrear} sx={{ textTransform: 'none' }}>
            Crear / configurar redondeo
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading || !selectedKey}
            sx={{ textTransform: 'none' }}
          >
            Confirmar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SeleccionarMcpRedondeoDialog;
